"use client";

import Link from "next/link";
import { useEffect } from "react";
import { reportError } from "@/lib/error-reporting";

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

// biome-ignore lint/suspicious/noShadowRestrictedNames: Next.js convention for error boundary component name
export default function Error({ error, reset }: ErrorProps) {
  useEffect(() => {
    // Report error to error tracking service (e.g. Sentry)
    reportError(error, {
      context: "ErrorBoundary",
      digest: error.digest,
    });
  }, [error]);

  return (
    <div className="error-boundary">
      <div className="error-content">
        <div className="error-icon">⚠️</div>
        <h1>Something went wrong</h1>
        <p>
          We encountered an unexpected error while loading this page. Please try
          again or return to the homepage.
        </p>
        {process.env.NODE_ENV === "development" && (
          <details className="error-details">
            <summary>Error Details</summary>
            <pre>{error.message}</pre>
            {error.digest && <p>Error ID: {error.digest}</p>}
          </details>
        )}
        <div className="error-actions">
          <button
            className="error-button primary"
            onClick={reset}
            type="button"
          >
            Try Again
          </button>
          <Link className="error-button secondary" href="/">
            Go to Homepage
          </Link>
        </div>
      </div>

      <style jsx>{`
        .error-boundary {
          min-height: 60vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }

        .error-content {
          text-align: center;
          max-width: 500px;
        }

        .error-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
        }

        h1 {
          font-size: 1.75rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          color: var(--cfx-accent);
        }

        p {
          color: var(--cfx-text-muted);
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }

        .error-details {
          text-align: left;
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: var(--cfx-surface-2);
          border-radius: var(--cfx-radius-btn);
        }

        .error-details summary {
          cursor: pointer;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }

        .error-details pre {
          font-size: 0.875rem;
          overflow-x: auto;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .error-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .error-button {
          padding: 0.75rem 1.5rem;
          border-radius: var(--cfx-radius-btn);
          font-weight: 500;
          font-size: 0.9375rem;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
          display: inline-block;
        }

        .error-button.primary {
          background: var(--cfx-accent);
          color: white;
          border: none;
        }

        .error-button.primary:hover {
          opacity: 0.9;
        }

        .error-button.secondary {
          background: transparent;
          color: var(--cfx-text);
          border: 1px solid var(--cfx-border);
        }

        .error-button.secondary:hover {
          background: var(--cfx-surface-2);
        }

        @media (prefers-color-scheme: dark) {
          h1 {
            color: var(--cfx-accent);
          }

          p {
            color: var(--cfx-text-muted);
          }

          .error-button.secondary {
            border-color: var(--cfx-border);
            color: var(--cfx-text);
          }

          .error-button.secondary:hover {
            background: var(--cfx-surface-2);
          }
        }
      `}</style>
    </div>
  );
}
